/**
 * History-state presentation — Living Brief LB4.
 *
 * The state itself is assigned server-side (`backend/src/utils/living-brief-history-state.ts`)
 * and travels on the disposition record. This module only decides how it looks.
 *
 * The label comes from the server when it sent one, so the four strings live in
 * exactly one place; the map below is the fallback for a record saved before the
 * server started labelling, and for the PATCH response, which does not recompute
 * a state for a row the operator just edited.
 */

import type { PlanV2HistoryState, PlanV2IssueDispositionRecord } from './plan-v2-issues';

export const PLAN_V2_HISTORY_STATE_LABELS: Record<PlanV2HistoryState, string> = {
  still_open: 'Still open',
  looks_resolved: 'Looks resolved',
  no_longer_relevant: 'No longer relevant',
  too_early_to_tell: 'Too early to tell'
};

/**
 * Tone per state. Deliberately not a traffic light: "Looks resolved" is good
 * news, "No longer relevant" is neither good nor bad, and "Too early to tell"
 * is an absence of news — colouring the last two the same would collapse the
 * distinction the states exist to draw.
 */
export const PLAN_V2_HISTORY_STATE_TONE: Record<PlanV2HistoryState, string> = {
  still_open: 'open',
  looks_resolved: 'resolved',
  no_longer_relevant: 'moot',
  too_early_to_tell: 'unknown'
};

const KNOWN_STATES = new Set<string>(Object.keys(PLAN_V2_HISTORY_STATE_LABELS));

export function planV2HistoryState(
  item: Pick<PlanV2IssueDispositionRecord, 'historyState'>
): PlanV2HistoryState | null {
  const raw = item.historyState;
  return raw && KNOWN_STATES.has(raw) ? raw : null;
}

export function planV2HistoryStateLabel(
  item: Pick<PlanV2IssueDispositionRecord, 'historyState' | 'historyStateLabel'>
): string | null {
  const state = planV2HistoryState(item);
  if (!state) return null;
  return (item.historyStateLabel || '').trim() || PLAN_V2_HISTORY_STATE_LABELS[state];
}

/**
 * Tone name -> `.badge` tone, the only place this file knows about the
 * component layer. Neutral has no modifier -- it is what `.badge` already is
 * -- which is why this returns the whole class list rather than a modifier:
 * an empty string would be indistinguishable from the null that means "this
 * record has no history state at all".
 *
 * `unknown` is --muted rather than a colour. It was a dashed grey outline
 * before the migration, and a dashed border is not something `.badge` offers;
 * de-emphasis is the closest honest reading of "too early to tell", and the
 * label still says it in words.
 */
const BADGE_TONE: Record<string, string> = {
  open: '',
  resolved: 'badge--success',
  moot: 'badge--warning',
  unknown: 'badge--muted'
};

export function planV2HistoryStateChipClass(
  item: Pick<PlanV2IssueDispositionRecord, 'historyState'>
): string | null {
  const state = planV2HistoryState(item);
  if (!state) return null;
  const tone = BADGE_TONE[PLAN_V2_HISTORY_STATE_TONE[state]];
  return `badge badge--pill${tone ? ` ${tone}` : ''}`;
}

/**
 * The evidence line under the chip.
 *
 * Never falls back to the label. A chip reading "Looks resolved" over a line
 * reading "looks resolved" tells the operator nothing they can check, and
 * checking is the entire job of a compare label.
 */
export function planV2HistoryStateReason(
  item: Pick<PlanV2IssueDispositionRecord, 'historyState' | 'historyStateReason'>
): string | null {
  if (!planV2HistoryState(item)) return null;
  return (item.historyStateReason || '').trim() || null;
}
